import { ReactNode } from 'react';
import { eventWhen } from '../../lib/dates';
import { withTime } from '../../lib/dates';

export interface TimelineEntry {
  id: number | string;
  timestamp: string;
  actor: string | null;
  body: ReactNode;
  /** Design `.evi`: a 36px icon tile per event. Without an icon the entry keeps the plain accent dot. */
  icon?: ReactNode;
  /** `hot` = escalations (accent tile), `ok` = assignments / outcomes (success tile). */
  tone?: 'default' | 'hot' | 'ok';
  /** Bold heading before the "actor · when" line (icon entries only). */
  title?: ReactNode;
}

interface Props {
  entries: TimelineEntry[];
  className?: string;
}

function fmtTimestamp(iso: string): string {
  try {
    return withTime(iso);
  } catch {
    return iso;
  }
}

const TILE = {
  default: 'bg-mist text-slate-ink',
  hot: 'bg-accent-line text-accent-ink',
  ok: 'bg-success-bg text-success',
} as const;

export default function TimelineRail({ entries, className = '' }: Props) {
  if (entries.length === 0) {
    return <p className="px-2 py-4 text-sm italic text-muted">No events yet.</p>;
  }
  if (entries.some((e) => e.icon)) {
    // Design `.rail`: icon tiles joined by a 2px connector.
    return (
      <ol className={`m-0 flex list-none flex-col p-0 ${className}`}>
        {entries.map((e) => (
          <li key={e.id} className="relative flex gap-3.5 pb-5 last:pb-0 [&:not(:last-child)]:before:absolute [&:not(:last-child)]:before:bottom-0.5 [&:not(:last-child)]:before:left-[17px] [&:not(:last-child)]:before:top-[38px] [&:not(:last-child)]:before:w-0.5 [&:not(:last-child)]:before:bg-track [&:not(:last-child)]:before:content-['']">
            <span aria-hidden="true" className={`grid h-9 w-9 flex-none place-items-center rounded-[12px] ${TILE[e.tone ?? 'default']}`}>{e.icon}</span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1.5">
                {e.title && <span className="font-semibold text-ink">{e.title}</span>}
                <span className="text-[13px] text-muted">
                  {e.actor && <>{e.actor} · </>}<time dateTime={e.timestamp}>{eventWhen(e.timestamp)}</time>
                </span>
              </div>
              {e.body}
            </div>
          </li>
        ))}
      </ol>
    );
  }
  return (
    <ol className={`relative ml-3 border-l-2 border-line-strong pl-6 ${className}`}>
      {entries.map((e) => (
        <li key={e.id} className="relative mb-6 last:mb-0">
          <span className="absolute -left-[33px] top-1.5 h-3 w-3 rounded-full border-2 border-surface bg-accent" />
          <p className="font-mono text-xs text-muted">
            {fmtTimestamp(e.timestamp)}
            {e.actor && <span> · {e.actor}</span>}
          </p>
          <div className="mt-1">{e.body}</div>
        </li>
      ))}
    </ol>
  );
}
